import React from 'react';
import { EmptyState, Button, Spinner } from '@parsina/framework';
import { UsersRound, MessageSquare } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useCases } from '../../hooks/useCases.js';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { bffQuery, errorCode } from '../../lib/bffClient.js';
import { CASE_CHAT_THREADS_QUERY } from '../../graphql/documents.js';
import ErrorState from '../../components/common/ErrorState.jsx';
import { PracticeBadge } from '../../components/common/PracticeNotice.jsx';
import { patientName, threadAt, PATIENT_SCOPE } from './ChatLanding.shared.js';

// «گفتگو» in the navigation — the one chat entry point that is not about a
// patient yet. core.md §7.6.
//
//   * a thread exists somewhere → resume the most recent one;
//   * patients but no threads   → «دربارهٔ کدام بیمار می‌خواهید گفتگو کنید؟»;
//   * no patients at all        → «بیمار جدید», because a conversation about
//                                 nobody is not a conversation.
//
// ── The contract has no "my most recent thread" query, on purpose ────────
//
// Every list in this app is scoped to the student and NOT searchable across
// rows: a cross-patient query is the shape that becomes cross-STUDENT by
// dropping one predicate (core.md §3.2). So "the most recent conversation" is
// assembled here from two calls the contract does have: `myCases` (already
// carrying `threadCount`, so patients with no conversation are never asked
// about) and one `caseChatThreads` for the most recently touched candidate.
//
// The candidate is picked by the patient's own recency, which is a heuristic
// and not a fact: a student who chatted about patient B this morning and edited
// patient A's history this afternoon lands on A. That is a resumption, not a
// destination — the sidebar is one click away — and the honest alternative
// (fetching threads for every patient) is N round trips for a redirect.

export default function ChatLandingScreen() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  // Every status: a conversation about an archived patient is still readable
  // (core.md §7.4 — archiving hides a patient from the working list, it does
  // not take their record away).
  const { cases, loading, error, reload } = useCases({ statuses: [] });

  const [resolving, setResolving] = React.useState(true);
  const [failed, setFailed] = React.useState(null);

  const withThreads = React.useMemo(
    () => (cases || [])
      .filter((row) => Number(row?.threadCount || 0) > 0)
      .sort((a, b) => Number(b?.updatedAtMs || 0) - Number(a?.updatedAtMs || 0)),
    [cases],
  );

  React.useEffect(() => {
    if (loading) return undefined;
    const candidate = withThreads[0];
    if (!candidate) { setResolving(false); return undefined; }

    let alive = true;
    setResolving(true);
    bffQuery(CASE_CHAT_THREADS_QUERY, {
      caseId: candidate.id, scope: PATIENT_SCOPE, encounterId: null,
    })
      .then((data) => {
        if (!alive) return;
        const newest = [...(data?.caseChatThreads || [])].sort((a, b) => threadAt(b) - threadAt(a))[0];
        if (newest) {
          // REPLACE: `/chat` is a redirector, and a back button that returns to
          // a redirector re-redirects — a trap the student cannot leave.
          navigate('chat', { caseId: candidate.id, threadId: newest.id, replace: true });
          return;
        }
        setResolving(false);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[chat-landing] failed:', errorCode(err) || err?.message);
        // A failed lookup falls back to the PICKER rather than to an error
        // page: the student can still choose a patient, which is all this
        // screen was ever going to do for them.
        setFailed(err);
        setResolving(false);
      });
    return () => { alive = false; };
  }, [loading, withThreads, navigate]);

  if (error) {
    return <ErrorState error={error} onRetry={reload} fill />;
  }

  if (loading || resolving) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner label={t('common.labels.loading')} />
      </div>
    );
  }

  if (!cases.length) {
    return (
      <EmptyState
        icon={<UsersRound size={44} strokeWidth={1.5} aria-hidden />}
        title={t('patients.empty.title')}
        description={t('chat.landing.body')}
        action={<Button onClick={() => navigate('patients')}>{t('chat.landing.new')}</Button>}
        fill
        data-testid="chat-landing-empty"
      />
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 720, margin: '0 auto' }} data-testid="chat-landing-picker">
      <h1 style={{ fontSize: 18, fontWeight: 800, color: C.textPrimary, margin: '0 0 6px' }}>
        {t('chat.landing.title')}
      </h1>
      <p style={{ fontSize: 13, color: CT.ink2, lineHeight: 1.9, margin: '0 0 16px' }}>
        {t('chat.landing.body')}
      </p>
      {failed ? (
        <p role="alert" style={{ fontSize: 12.5, color: CT.dangerInk, margin: '0 0 12px' }}>
          {t('errors.unavailable')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: 8 }}>
        {cases.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => navigate('chat', { caseId: row.id })}
            data-testid="chat-landing-case"
            aria-label={t('chat.landing.open', { name: patientName(row) })}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              font: 'inherit', fontSize: 13.5, textAlign: 'start', cursor: 'pointer',
              padding: '12px 14px', borderRadius: 12,
              border: `1px solid ${C.border}`, background: C.card, color: C.textPrimary,
            }}
          >
            <MessageSquare size={17} color={C.teal} aria-hidden />
            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {patientName(row)}
            </span>
            <PracticeBadge short />
          </button>
        ))}
      </div>
    </div>
  );
}
